import classNames from 'classnames';
import React, { forwardRef, Ref, useRef, useState } from 'react';
import { getZIndex } from '../../styles';
import { AriaFocusProps } from '../../types/common';
import { useProviderConfig } from '../provider';
import { DropdownChipLabelOption, DropdownChipLabel } from './DropdownChipLabel';
import { DEFAULT_DROPDOWN_MULTI_SELECT_MAX_HEIGHT } from './constants';
import { DropdownProps } from './Dropdown';
import { DropdownFilterOptions } from './DropdownFilterOptions';
import { useDropdownFloating, useDropdownInteractions } from './hooks';
import { OpenChangeReason, useMergeRefs } from '@floating-ui/react';

export interface DropdownMultiSelect<OptionValue>
  extends Omit<DropdownProps<OptionValue>, 'options' | 'renderLabel' | 'value' | 'onChange' | 'maxHeight'> {
  value?: OptionValue[];
  options: DropdownChipLabelOption<OptionValue>[];
  /**
   * Dropdown OptionList maxHeight값
   * - size가 large일 경우 default 높이는 none, 그외에는 기본 값이 적용되며 사용처에서 재정의할 수 있습니다.
   *
   * @default DEFAULT_DROPDOWN_MULTI_SELECT_MAX_HEIGHT
   */
  maxHeight?: number;
  /**
   * DropdownFilter Option들의 전체 선택 여부
   *
   * @default false
   */
  allCheck?: boolean;
  onChange?: (value: OptionValue[]) => void;
}

interface DropdownMultiSelectOpenState {
  openByRef: boolean;
  openByDeleteChip: boolean;
}

function DropdownMultiSelectInner<OptionValue extends string | number>(
  {
    value: valueProp = [],
    options = [],
    className,
    placeholder,
    status,
    disabled,
    width,
    size: sizeProp = 'medium',
    optionsMatchRefWidth,
    maxHeight,
    zIndex = getZIndex('dropdown'),
    autoPlacement: autoPlacementProps = true,
    unstableFocusVisible,
    allCheck = false,
    keyExtractor,
    onChange,
  }: DropdownMultiSelect<OptionValue> & AriaFocusProps,
  ref: Ref<HTMLDivElement>,
) {
  const { locale } = useProviderConfig();
  const [openState, setOpenState] = useState<DropdownMultiSelectOpenState>({
    openByRef: false,
    openByDeleteChip: false,
  });
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const optionListRef = useRef<Array<HTMLElement | null>>([]);
  const selectedIndex = options.findIndex(({ value }) => value === valueProp[valueProp.length - 1]);
  const open = openState.openByRef && !openState.openByDeleteChip;
  const isAllCheck = valueProp.length === options.length;
  const handleClose = () => {
    setOpenState({ openByRef: false, openByDeleteChip: false });
  };
  const handleFloatingOpenChange = (open: boolean, evt?: MouseEvent, reason?: OpenChangeReason) => {
    if (reason === 'outside-press') {
      handleClose();
    }
  };

  const { refs, floatingStyles, context } = useDropdownFloating({
    open,
    onOpenChange: handleFloatingOpenChange,
    optionsMatchRefWidth,
    autoPlacement: autoPlacementProps,
    maxHeight: maxHeight ?? (sizeProp !== 'large' ? DEFAULT_DROPDOWN_MULTI_SELECT_MAX_HEIGHT : undefined),
  });
  const referenceRef = useMergeRefs([refs.setReference, ref]);

  const { getReferenceProps, getFloatingProps, getItemProps } = useDropdownInteractions({
    context,
    useClickProps: {
      enabled: !disabled,
    },
    useListNavigationProps: {
      listRef: optionListRef,
      activeIndex: focusIndex,
      selectedIndex,
      onNavigate: setFocusIndex,
    },
    disabledInteractions: ['useTypeahead'],
  });

  const handleChange = (item_value: OptionValue) => {
    const is_checked = !valueProp.includes(item_value);
    const new_values = is_checked
      ? [...valueProp, item_value]
      : valueProp.filter((value) => String(value) !== String(item_value));
    onChange?.(new_values);
  };

  const handleAllCheck = () => {
    isAllCheck ? onChange?.([]) : onChange?.(options.map(({ value }) => value));
  };

  return (
    <>
      <DropdownChipLabel
        {...getReferenceProps({
          'ref': referenceRef,
          'className': classNames('pds-dropdown-multi-select-label', className),
          'aria-label': placeholder ?? locale?.Dropdown.placeholder,
          width,
          disabled,
          'tabIndex': 0,
          'selected': valueProp.length > 0,
          'onClick': () => {
            if (!disabled) {
              setOpenState((prev) => ({ openByRef: !prev.openByRef, openByDeleteChip: false }));
            }
          },
          'onKeyDown'(event) {
            if (event.key === 'Enter' || event.key === 'Tab') {
              setOpenState({ openByRef: true, openByDeleteChip: false });
              setFocusIndex(0);
            }
          },
        })}
        expanded={open}
        isAllCheck={allCheck && isAllCheck}
        placeholder={placeholder}
        status={status}
        size={sizeProp}
        selectedOptions={valueProp.map((value) => {
          return { value, label: options.find((option) => option.value === value)?.label ?? '' };
        })}
        onOptionValuesChange={(options) => {
          onChange?.(options as OptionValue[]);
          setOpenState({ openByRef: false, openByDeleteChip: true });
        }}
      />
      <DropdownFilterOptions
        refs={refs}
        selectedList={valueProp}
        kind='checkbox'
        showFilter={false}
        size={sizeProp}
        setRefs={(node, index) => {
          optionListRef.current[index] = node;
        }}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        getItemProps={getItemProps}
        onClose={() => {
          setFocusIndex(null);
          handleClose();
        }}
        zIndex={zIndex}
        unstableFocusVisible={unstableFocusVisible}
        keyExtractor={keyExtractor}
        context={context}
        focusIndex={focusIndex}
        isAllCheck={allCheck}
        options={options}
        value={valueProp}
        onChange={handleChange}
        onAllCheck={handleAllCheck}
        setFocusIndex={setFocusIndex}
        showConfirm={false}
      />
    </>
  );
}

export const DropdownMultiSelect = forwardRef(DropdownMultiSelectInner) as (<OptionValue extends string | number>(
  props: DropdownMultiSelect<OptionValue> & AriaFocusProps & { ref?: Ref<HTMLDivElement> },
) => JSX.Element) & { displayName?: string };

DropdownMultiSelect.displayName = 'DropdownMultiSelect';
